import { Grid, Divider, Switch, FormControlLabel, Typography } from '@mui/material';
import { useState } from 'react';
import { WsColors, WsTypoColors, WsTypography, WsTypoVariants } from 'wisestep-base';

import CardWrapper from '../../../../features/automatedEmailCampaignSettings/childRoutes/CardWrapper';
import {
  CompressedLink,
  ExpandableChips,
  ExpandableTypography,
} from '../../../../features/candidateLayout/components/childComponets';
import { isValidDash } from '../../../../features/candidateLayout/utils/CandidateHelper';

export interface InfoDetail {
  label: string;
  value: string | string[];
  type: INFO_TYPE;
}

interface InfoCardProps {
  title: string;
  details: InfoDetail[];
  enableMissingInfoToggle?: boolean;
}

export enum INFO_TYPE {
  TEXT,
  LINK,
  CHIP,
}

const renderComponent = (type: INFO_TYPE, value: string | string[], label: string) => {
  const val = Array.isArray(value) ? value?.[0] : value;
  if (val === '-') return <ExpandableTypography content={val} />;
  switch (type) {
    case INFO_TYPE.TEXT:
      return <ExpandableTypography content={value as string} />;

    case INFO_TYPE.LINK: {
      const [label, url] = value as string[];
      return <CompressedLink label={label} url={url} />;
    }

    case INFO_TYPE.CHIP: {
      const chips = Array.isArray(value) ? value : [value];
      return <ExpandableChips items={chips} label={label} />;
    }

    default:
      return <Typography>{value}</Typography>;
  }
};

const InfoDetailsCard: React.FC<InfoCardProps> = ({
  title,
  details,
  enableMissingInfoToggle = true,
}) => {
  const [showMissingInfo, setShowMissingInfo] = useState<boolean>(false);

  const toggleInfo = () => setShowMissingInfo((prev) => !prev);

  return (
    <CardWrapper>
      <Grid p={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {title}
        </WsTypography>
      </Grid>

      <Divider sx={{ borderColor: WsColors.NEUTRAL_100 }} />

      <Grid display="flex" direction="column" xs={12} p={1} rowGap={1}>
        {details
          .filter(({ value }) => showMissingInfo || !isValidDash(value))
          .map(({ label, value, type }, index) => (
            <Grid display="flex" alignItems="center" key={index} columnGap={1}>
              <Grid item xs={6}>
                <WsTypography color={WsTypoColors.TEXTLIGHT}>{label}</WsTypography>
              </Grid>
              <Grid item xs={6}>
                {renderComponent(type, value, label)}
              </Grid>
            </Grid>
          ))}

        {enableMissingInfoToggle && (
          <Grid xs={12}>
            <FormControlLabel
              sx={{ columnGap: 1, m: 0 }}
              control={<Switch checked={showMissingInfo} onChange={toggleInfo} />}
              label="Show missing info"
              labelPlacement="start"
            />
          </Grid>
        )}
      </Grid>
    </CardWrapper>
  );
};

export default InfoDetailsCard;
